/**
 * A requester's accepted types — the strings a plugin passes to
 * `pick({ accept })`, in the HTML `accept` idiom: MIME types
 * (`application/pdf`), MIME families (`image/*`) and extensions (`.docx`) —
 * turned into the dialogs' `FileAccept`.
 *
 * Kinds are filled in only when EVERY entry maps to one, because the server
 * filters by kind: an extension (".docx") says nothing about the MIME type a
 * row was stored with, so a kind filter derived from the rest would hide it.
 */
import { mimeToFileKind, type VaultFileKind } from '@/lib/vault-file-kinds';
import type { FileAccept } from './useFileBrowser';

const FAMILY_KINDS: Record<string, VaultFileKind> = {
  'image/*': 'image',
  'audio/*': 'audio',
  'video/*': 'video',
};

/** The accept strings a pick may carry: at most 20, each a MIME type, a
 *  MIME family or an extension. */
export const ACCEPT_ENTRY_RE =
  /^(?:\.[a-z0-9][a-z0-9.+-]{0,15}|[a-z]+\/(?:\*|[a-z0-9][a-z0-9.+-]{0,99}))$/i;
export const MAX_ACCEPT_ENTRIES = 20;

export function fileAcceptFromTypes(types: readonly string[] | undefined): FileAccept | undefined {
  if (!types || types.length === 0) return undefined;
  const mimeTypes: string[] = [];
  const extensions: string[] = [];
  const kinds = new Set<VaultFileKind>();
  let everyEntryHasAKind = true;
  for (const raw of types) {
    const entry = raw.trim().toLowerCase();
    if (!entry) continue;
    if (entry.startsWith('.')) {
      extensions.push(entry);
      everyEntryHasAKind = false;
      continue;
    }
    mimeTypes.push(entry);
    const kind = entry.endsWith('/*') ? FAMILY_KINDS[entry] : mimeToFileKind(entry);
    if (kind && kind !== 'other') kinds.add(kind);
    else everyEntryHasAKind = false;
  }
  return {
    ...(everyEntryHasAKind && kinds.size > 0 ? { kinds: [...kinds] } : {}),
    ...(mimeTypes.length ? { mimeTypes } : {}),
    ...(extensions.length ? { extensions } : {}),
  };
}
